import React from 'react';
import { Mail, MessageSquare, Phone, File, Check, Clock, AlertTriangle } from "lucide-react";
import { Communication } from "./types";

export const getTypeIcon = (type: string) => {
  switch (type) {
    case "email": return <Mail className="h-4 w-4" />;
    case "chat": return <MessageSquare className="h-4 w-4" />;
    case "call": return <Phone className="h-4 w-4" />;
    default: return <File className="h-4 w-4" />;
  }
};

export const getStatusBadge = (status: string, flagged: boolean) => {
  if (flagged) {
    return <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700">
      <AlertTriangle className="h-3 w-3" /> Flagged
    </span>;
  }
  
  switch (status) {
    case "archived":
      return <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">
        <Check className="h-3 w-3" /> Archived
      </span>;
    case "pending_review":
      return <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700">
        <Clock className="h-3 w-3" /> Pending
      </span>;
    default:
      return <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100">{status}</span>;
  }
};

export const formatDate = (date: Date) => {
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  
  if (diff < 1000 * 60 * 60) {
    const minutes = Math.floor(diff / (1000 * 60));
    return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;
  } else if (diff < 1000 * 60 * 60 * 24) {
    const hours = Math.floor(diff / (1000 * 60 * 60));
    return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
  } else {
    return date.toLocaleDateString();
  }
};

export const filterCommunications = (
  communications: Communication[],
  selectedTab: string,
  selectedStatus: string,
  searchTerm: string,
  timeRange: string
) => {
  return communications.filter(comm => {
    // Filter by tab/type
    if (selectedTab !== "all" && comm.type !== selectedTab) {
      return false;
    }
    
    // Filter by status
    if (selectedStatus !== "all") {
      if (selectedStatus === "flagged" && !comm.flagged) {
        return false;
      } else if (selectedStatus === "pending" && comm.status !== "pending_review") {
        return false;
      } else if (selectedStatus === "archived" && comm.status !== "archived") {
        return false;
      }
    }
    
    // Filter by search term
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      return (
        comm.sender.toLowerCase().includes(searchLower) ||
        comm.recipient.toLowerCase().includes(searchLower) ||
        comm.subject.toLowerCase().includes(searchLower) ||
        comm.content.toLowerCase().includes(searchLower)
      );
    }
    
    // Filter by time range
    const now = new Date();
    const commDate = comm.timestamp;
    
    if (timeRange === "24h") {
      return (now.getTime() - commDate.getTime()) <= 1000 * 60 * 60 * 24;
    } else if (timeRange === "7d") {
      return (now.getTime() - commDate.getTime()) <= 1000 * 60 * 60 * 24 * 7;
    } else if (timeRange === "30d") {
      return (now.getTime() - commDate.getTime()) <= 1000 * 60 * 60 * 24 * 30;
    }
    
    return true;
  });
};

// Mock data for communications
export const mockCommunications: Communication[] = [
  {
    id: 1,
    type: "email",
    sender: "john.doe@client.com",
    recipient: "advisor@firm.com",
    subject: "Portfolio Reallocation Request",
    timestamp: new Date(Date.now() - 1000 * 60 * 30), // 30 minutes ago
    status: "archived",
    content: "I'd like to discuss reallocating my portfolio to include more tech stocks...",
    attachments: 1,
    flagged: false
  },
  {
    id: 2,
    type: "chat",
    sender: "sarah.smith@client.com",
    recipient: "advisor@firm.com",
    subject: "Trading Strategy Question",
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 2), // 2 hours ago
    status: "archived",
    content: "Can we discuss the new trading strategy you mentioned last week?",
    attachments: 0,
    flagged: false
  },
  {
    id: 3,
    type: "call",
    sender: "robert.johnson@client.com",
    recipient: "advisor@firm.com",
    subject: "Account Review Call",
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 5), // 5 hours ago
    status: "pending_review",
    content: "Call recording regarding quarterly account review and investment strategy",
    attachments: 0,
    flagged: true
  },
  {
    id: 4,
    type: "email",
    sender: "advisor@firm.com",
    recipient: "michael.brown@client.com",
    subject: "Investment Recommendation",
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24), // 1 day ago
    status: "archived",
    content: "Based on our discussion, I recommend the following investment options...",
    attachments: 2,
    flagged: false
  },
  {
    id: 5,
    type: "chat",
    sender: "advisor@firm.com",
    recipient: "emily.wilson@client.com",
    subject: "Risk Tolerance Assessment",
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24 * 2), // 2 days ago
    status: "pending_review",
    content: "Let's discuss your risk tolerance assessment results and next steps.",
    attachments: 1,
    flagged: true
  },
  {
    id: 6,
    type: "email",
    sender: "compliance@firm.com",
    recipient: "advisor@firm.com",
    subject: "Monthly Compliance Review",
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24 * 3), // 3 days ago
    status: "flagged",
    content: "The following communications need additional review for compliance...",
    attachments: 0,
    flagged: true
  },
  {
    id: 7,
    type: "call",
    sender: "advisor@firm.com",
    recipient: "david.clark@client.com",
    subject: "Portfolio Performance Discussion",
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24 * 5), // 5 days ago
    status: "archived",
    content: "Call recording discussing Q2 portfolio performance and market outlook",
    attachments: 1,
    flagged: false
  }
];
